import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { callAPI } from '../utils/apiUtils';
import { apiUrls } from '../utils/apiUrls';
import { toast } from 'react-toastify';
import CustomerScreenCard from '../components/CustomerScreenCard';
import PerformanceCard from '../components/PerformanceCard';
import LocalFirstCard from '../components/LocalFirstCard';
import { relayCustomerScreen } from '../utils/customerDisplayOptions';

/**
 * Per-location feature toggles for this restaurant. The list itself lives on the server
 * (ConfigsController::POS_SETTINGS) - this page only renders whatever it is handed, so adding a
 * new toggle later is a backend-only change: one entry in that array, nothing here.
 */
const SettingsPage = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  // Draft text for a duration_seconds setting's "Custom" number input, keyed by setting.key -
  // kept separate from `settings` state so typing a custom value doesn't save on every keystroke;
  // it only commits on blur/Enter.
  const [customDrafts, setCustomDrafts] = useState({});
  // Local, per-device preference - deliberately NOT part of the server-driven `settings` list
  // above (see the module comment): it must never apply "for every client"/permanently, just this
  // browser. Mirrors the localStorage half of LayoutModeToggle.js's sync pattern, minus the API
  // call that makes that one a server setting cached locally rather than a true local-only one.
  const [hideChairs, setHideChairs] = useState(localStorage.getItem('hide_floor_plan_chairs') === '1');
  // Instant cart (this device only): on unless switched off. Read by useCart on every edit, so it
  // takes effect straight away with no reload. A kill switch for this device if it ever misbehaves.
  const [instantCart, setInstantCart] = useState(localStorage.getItem('cart_optimistic') !== '0');

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      navigate('/');
      return;
    }

    (async () => {
      try {
        const res = await callAPI(apiUrls.POS_SETTINGS_GET, {}, 'GET');
        setSettings(res?.data?.data || []);
      } catch (e) {
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const toggle = async (key, nextValue) => {
    // Optimistic - a manager flipping this switch should see it move immediately, not wait on a
    // round trip. Reverted below if the save fails.
    setSettings((prev) => prev.map((s) => (s.key === key ? { ...s, value: nextValue } : s)));
    setSavingKey(key);
    try {
      await callAPI(apiUrls.POS_SETTINGS_UPDATE, {}, 'POST', { [key]: nextValue ? 1 : 0 }, {});
      // the customer screen's "join rewards" QR follows this switch: ask the server again and tell the second screen now
      if (key === 'customer_screen_join_qr') relayCustomerScreen();
      // the menu keeps this device's last answer (see useMenuCardSettings) so it is drawn the right way at once
      if (key === 'compact_product_cards' || key === 'hide_allergen_icons') localStorage.setItem(key, nextValue ? '1' : '0');
    } catch (e) {
      setSettings((prev) => prev.map((s) => (s.key === key ? { ...s, value: !nextValue } : s)));
      toast.error('Could not save that setting. Please try again.');
    } finally {
      setSavingKey(null);
    }
  };

  const saveDuration = async (key, prevValue, seconds) => {
    if (!Number.isFinite(seconds) || seconds < 1) {
      toast.error('Enter a whole number of seconds greater than 0.');
      return;
    }
    setSettings((prev) => prev.map((s) => (s.key === key ? { ...s, value: seconds } : s)));
    setSavingKey(key);
    try {
      await callAPI(apiUrls.POS_SETTINGS_UPDATE, {}, 'POST', { [key]: seconds }, {});
    } catch (e) {
      setSettings((prev) => prev.map((s) => (s.key === key ? { ...s, value: prevValue } : s)));
      toast.error('Could not save that setting. Please try again.');
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <MainLayout>
      <div className="pageContent">
        <div className="container">
          <h2 className="lgTitle mb-3">Settings</h2>

          <div className="cardStyle mb-4">
            <div className="cardBody d-flex align-items-start justify-content-between gap-3">
              <div>
                <h5 className="mb-1">Hide floor plan chairs</h5>
                <p className="mb-0" style={{ opacity: 0.7, fontSize: 13 }}>
                  Hides the chair icons around tables on the floor plan. This device only — it does
                  not change anything for other devices or other staff.
                </p>
              </div>
              <div className="form-check form-switch mb-0 flex-shrink-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  checked={hideChairs}
                  onChange={(e) => {
                    const next = e.target.checked;
                    localStorage.setItem('hide_floor_plan_chairs', next ? '1' : '0');
                    window.dispatchEvent(new Event('floorPlanChairsVisibilityChanged'));
                    setHideChairs(next);
                  }}
                />
              </div>
            </div>
          </div>

          <div className="cardStyle mb-4">
            <div className="cardBody d-flex align-items-start justify-content-between gap-3">
              <div>
                <h5 className="mb-1">Instant cart</h5>
                <p className="mb-0" style={{ opacity: 0.7, fontSize: 13 }}>
                  Items, Half n Halfs, quantities and removals show in the cart the moment you tap, while the server
                  catches up, and the Deal and Half n Half sheets open from stored data. Switch off to wait for the
                  server every time. This device only.
                </p>
              </div>
              <div className="form-check form-switch mb-0 flex-shrink-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  checked={instantCart}
                  onChange={(e) => {
                    const next = e.target.checked;
                    localStorage.setItem('cart_optimistic', next ? '1' : '0');
                    setInstantCart(next);
                  }}
                />
              </div>
            </div>
          </div>

          {/* Restaurant-wide but not part of the server-driven list below, so it renders whether or
              not that list has loaded (it loads its own state). */}
          <CustomerScreenCard />

          <LocalFirstCard />

          <PerformanceCard />

          {loading ? (
            <p>Loading…</p>
          ) : settings.length === 0 ? (
            <div className="cardStyle mb-4">
              <div className="cardBody">No configurable features yet.</div>
            </div>
          ) : (
            settings.map((setting) => {
              if (setting.type === 'duration_seconds') {
                // Only meaningful once its sibling enable/disable toggle is on, if one is
                // present in the list - grey it out rather than hide it, so it's clear the
                // control exists but isn't currently in effect.
                const gateSetting = settings.find((s) => s.key === 'staff_lock_screen_enabled');
                const isGated = !!gateSetting && !gateSetting.value;
                // Showing the custom input is "the saved value isn't a preset" OR "the user just
                // picked Custom from the dropdown" - the latter has no saved value to derive from
                // yet, hence the separate draft-presence check.
                const hasDraft = customDrafts[setting.key] !== undefined;
                const isCustom = hasDraft || !setting.options.includes(setting.value);
                const draft = customDrafts[setting.key] ?? String(setting.value);
                const commitCustom = () => {
                  const seconds = parseInt(draft, 10);
                  saveDuration(setting.key, setting.value, seconds);
                };

                return (
                  <div className="cardStyle mb-4" key={setting.key} style={isGated ? { opacity: 0.5 } : undefined}>
                    <div className="cardBody d-flex align-items-start justify-content-between gap-3 flex-wrap">
                      <div>
                        <h5 className="mb-1">{setting.title}</h5>
                        {setting.subtitle && (
                          <p className="mb-0" style={{ opacity: 0.7, fontSize: 13 }}>
                            {setting.subtitle}
                          </p>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-2 flex-shrink-0">
                        <select
                          className="form-select"
                          style={{ width: 140 }}
                          disabled={isGated || savingKey === setting.key}
                          value={isCustom ? 'custom' : setting.value}
                          onChange={(e) => {
                            if (e.target.value === 'custom') {
                              setCustomDrafts((prev) => ({ ...prev, [setting.key]: String(setting.value) }));
                              return;
                            }
                            setCustomDrafts((prev) => {
                              const next = { ...prev };
                              delete next[setting.key];
                              return next;
                            });
                            saveDuration(setting.key, setting.value, parseInt(e.target.value, 10));
                          }}
                        >
                          {setting.options.map((seconds) => (
                            <option key={seconds} value={seconds}>
                              {seconds} seconds
                            </option>
                          ))}
                          <option value="custom">Custom</option>
                        </select>
                        {isCustom && (
                          <input
                            type="number"
                            min="1"
                            className="form-control"
                            style={{ width: 100 }}
                            disabled={isGated || savingKey === setting.key}
                            value={draft}
                            onChange={(e) => setCustomDrafts((prev) => ({ ...prev, [setting.key]: e.target.value }))}
                            onBlur={commitCustom}
                            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div className="cardStyle mb-4" key={setting.key}>
                  <div className="cardBody d-flex align-items-start justify-content-between gap-3">
                    <div>
                      <h5 className="mb-1">{setting.title}</h5>
                      {setting.subtitle && (
                        <p className="mb-0" style={{ opacity: 0.7, fontSize: 13 }}>
                          {setting.subtitle}
                        </p>
                      )}
                    </div>
                    <div className="form-check form-switch mb-0 flex-shrink-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={!!setting.value}
                        disabled={savingKey === setting.key}
                        onChange={(e) => toggle(setting.key, e.target.checked)}
                      />
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default SettingsPage;
